"use client";
import { Button } from "@/components/ui/button";
import { CartContext } from "@/context/CartContext";
import { WishlistContext } from "@/context/WishlistContext";
import { useToast } from "@/hooks/use-toast";
import { trimText } from "@/lib/trimText";
import { formatCurrency } from "@/utils/formatCurrency";
import { CheckCircle2, Heart, ShoppingCart, XCircle } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useContext, useEffect, useState } from "react";
import ProductQuickView from "./ProductQuickView";

const DiscountBurst = ({ percent }) => {
  return (
    <div className="absolute -right-1 -top-1 h-14 w-14 drop-shadow-md z-10">
      <svg viewBox="0 0 100 100" className="h-full w-full">
        <polygon
          points="50,2 61,15 77,8 79,26 96,31 88,47 98,61 82,68 83,86 65,84 57,99 46,86 28,90 25,72 8,68 15,52 3,39 19,30 15,12 33,15"
          fill="#29b6f6"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white leading-none">
        <span className="text-xs font-extrabold">{percent}%</span>
        <span className="text-[7px] font-semibold">OFF</span>
      </div>
    </div>
  );
};

const StockStatus = ({ inStock }) => {
  return (
    <div className="flex items-center justify-center gap-1.5 py-2">
      {inStock ? (
        <>
          <CheckCircle2 size={14} className="text-green-600" />
          <span className="text-xs font-semibold text-green-700">In Stock</span>
        </>
      ) : (
        <>
          <XCircle size={14} className="text-red-500" />
          <span className="text-xs font-semibold text-red-600">Out of Stock</span>
        </>
      )}
    </div>
  );
};

const ProductCard = ({ product }) => {
  const { wishlist, toggleWishlist } = useContext(WishlistContext);
  const { addToCart } = useContext(CartContext);
  const { success, info } = useToast();
  const [wishlistProducts, setWishlistProducts] = useState([]);

  useEffect(() => {
    setWishlistProducts(wishlist);
  }, [wishlist]);

  const isInWishlist = wishlistProducts.some((item) => item?._id === product?._id);
  const isInStock = product?.countInStock > 0;
  const [imgSrc, setImgSrc] = useState(product?.images?.[0] || "/placeholder.jpg");

  const handleAddToCart = () => {
    if (!isInStock) {
      info("Contact us for product request");
    } else {
      success(`${trimText(product?.title, 20)} was added to the shopping cart.`);
      addToCart(product);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
          currency: "BDT",
          value: product?.discountedPrice,
          items: [
            {
              item_id: product?._id,
              item_name: product?.title,
              item_category: product?.category,
              item_subcategory: product?.subCategory,
              item_type: product?.type || product?.brand,
              item_variant: product?.variant || "",
              price: product?.discountedPrice,
              quantity: 1,
            },
          ],
        },
      });
    }
  };

  return (
    <div className="group relative flex h-[400px] flex-col border-r border-brand-light bg-white px-4 py-4 transition-colors duration-300 hover:bg-brand-light/30 last:border-r-0">
      <div className="relative h-[55%] w-full overflow-hidden rounded-lg bg-brand-light/40">
        <Link
          href={`/product/${product?.slug}`}
          
          className="block h-full w-full"
        >
          <div className="relative h-full w-full">
            <Image
              src={imgSrc}
              alt={product?.title || "Product Image"}
              fill
              sizes="(max-width: 768px) 100vw, 300px"
              className="object-contain transition-transform duration-500 group-hover:scale-105"
              onError={() => setImgSrc("/placeholder.jpg")}
            />
          </div>
        </Link>

        {product?.discount > 0 && <DiscountBurst percent={Math.ceil(product?.discount)} />}

        <div className="absolute inset-x-2 bottom-2 flex items-center justify-between opacity-0 transition-all duration-300 group-hover:opacity-100">
          <ProductQuickView
            product={product}
            onAddToCart={handleAddToCart}
          />

          <button
            onClick={() => toggleWishlist(product)}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-brand-sm transition-colors hover:bg-brand-light/60"
          >
            {isInWishlist ? (
              <Heart className="h-4 w-4 fill-red-500 text-red-500" />
            ) : (
              <Heart className="h-4 w-4 text-brand-navy" />
            )}
          </button>
        </div>
      </div>

      <div className="flex w-full flex-1 flex-col pt-3">
        <div>
          <Link
            href={`/product/${product?.slug}`}
            
            className="line-clamp-2 text-sm font-bold text-brand-navy hover:text-brand-sky transition-colors capitalize leading-tight"
          >
            {trimText(product?.title, 30)}
          </Link>
          <p className="line-clamp-1 text-xs font-medium text-brand-deep mt-0.5">
            {trimText(product?.author?.name, 20)}
          </p>
        </div>

        <div className="border-t border-dotted border-brand-light mt-2">
          <StockStatus inStock={isInStock} />
        </div>

        <div className="flex flex-col items-center justify-center gap-0.5">
          {product?.discount > 0 && (
            <span className="text-xs text-muted-foreground font-medium line-through">
              {formatCurrency(product?.price)}
            </span>
          )}
          <p className="text-lg font-bold text-brand-deep">
            {product?.discount > 0
              ? formatCurrency(Math.round(product?.discountedPrice))
              : formatCurrency(product?.price)}
          </p>
        </div>

        <div className="w-full h-0 overflow-hidden transition-all duration-300 group-hover:h-10 group-hover:mt-2">
          <Button
            onClick={handleAddToCart}
            className="w-full gradient-primary hover:opacity-95 text-white rounded-xl h-10 font-semibold flex items-center justify-center gap-2 shadow-brand hover:shadow-brand-lg transition-all"
          >
            <ShoppingCart size={16} /> BUY NOW
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;